import React from 'react';
import { PlusIcon, TrashIcon, DocumentIcon, EyeIcon } from '@heroicons/react/24/outline';
import PasteUploadArea from '../PasteUploadArea';

const ProductManagement = ({
  productsData,
  availableProducts,
  showProductModal,
  setShowProductModal,
  handleAddProduct,
  handleRemoveProduct,
  handleProductSelect,
  handleFieldChange,
  handleFeeChange,
  calculateTotalFees,
  uploadingFiles,
  handleFileUpload
}) => {
  const renderFileField = (productIndex, field, productData, productKey) => {

    const existingFile = productData[field.label];
    console.log("productData", productData);
    const isUploading = uploadingFiles[`${productKey}_${field.label}`];



    return (
      <div className="space-y-2">
        {/* Existing file display */}
        {existingFile && (
          <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center space-x-2">
              <DocumentIcon className="h-4 w-4 text-green-600" />
              <span className="text-sm text-green-800">
                {typeof existingFile === 'string' ? 'File uploaded' : existingFile.name || 'File uploaded'}
              </span>
            </div>
            <div className="flex space-x-1">
              {typeof existingFile === 'string' && (
                <a
                  href={existingFile}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                  title="View file"
                >
                  <EyeIcon className="h-4 w-4" />
                </a>
              )}
              <button
                type="button"
                onClick={() => handleFieldChange(productKey, field.label, null)}
                className="text-red-600 hover:text-red-800"
                title="Remove file"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* File upload input */}
        <PasteUploadArea
          onFileSelect={(file) => handleFileUpload(productIndex, field.label, file)}
          accept="application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          maxSize={10 * 1024 * 1024}
          className="flex-1"
          dragDropText="Upload a file"
          pasteText="or paste from clipboard (Ctrl+V)"
          uploadedUrl={existingFile}
        >
        </PasteUploadArea>
        
        {existingFile && (
          <p className="text-xs text-gray-500">
            Upload a new file to replace the existing one
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Products</h3>
        <button
          type="button"
          onClick={handleAddProduct}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Product
        </button>
      </div>

      {Object.keys(productsData).length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No products added yet. Click "Add Product" to get started.
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(productsData).map(([productKey, productData]) => {
            const [productIndex] = productKey.split('_');
            const product = availableProducts.find(p => p._id === productData.id);
            
            if (!product) return null;

            return (
              <div key={productKey} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-medium text-gray-900">
                    {product.name || 'Unknown Product'}
                  </h4>
                  <button
                    type="button"
                    onClick={() => handleRemoveProduct(productIndex)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Fees Section */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Government Fees
                    </label>
                    <input
                      type="number"
                      value={productData['Government Fees'] || 0}
                      onChange={(e) => handleFeeChange(productKey, 'Government Fees', null, e.target.value)}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        product.fees?.governmentFees && !product.fees.governmentFees.isCustomizable 
                          ? 'bg-gray-100 cursor-not-allowed' 
                          : ''
                      }`}
                      disabled={product.fees?.governmentFees && !product.fees.governmentFees.isCustomizable}
                    />
                    {product.fees?.governmentFees && !product.fees.governmentFees.isCustomizable && (
                      <p className="text-xs text-gray-500 mt-1">This fee cannot be edited</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Professional Fees
                    </label>
                    <input
                      type="number"
                      value={productData['Professional Fees'] || 0}
                      onChange={(e) => handleFeeChange(productKey, 'Professional Fees', null, e.target.value)}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        product.fees?.professionalFees && !product.fees.professionalFees.isCustomizable 
                          ? 'bg-gray-100 cursor-not-allowed' 
                          : ''
                      }`}
                      disabled={product.fees?.professionalFees && !product.fees.professionalFees.isCustomizable}
                    />
                    {product.fees?.professionalFees && !product.fees.professionalFees.isCustomizable && (
                      <p className="text-xs text-gray-500 mt-1">This fee cannot be edited</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Fees
                    </label>
                    <div className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50">
                      ${calculateTotalFees(productKey).toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* Other Fees */}
                {product.fees?.otherFees && product.fees.otherFees.length > 0 && (
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Other Fees</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {product.fees.otherFees.map((fee, feeIndex) => (
                        <div key={feeIndex}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {fee.name}
                          </label>
                          <input
                            type="number"
                            value={productData['Other Fees']?.[fee.name] || 0}
                            onChange={(e) => handleFeeChange(productKey, 'Other Fees', fee.name, e.target.value)}
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              !fee.isCustomizable ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            disabled={!fee.isCustomizable}
                          />
                          {!fee.isCustomizable && (
                            <p className="text-xs text-gray-500 mt-1">This fee cannot be edited</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Required Fields */}
                {product.requiredFields && product.requiredFields.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Required Information</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {product.requiredFields.map((field, fieldIndex) => (
                        <div key={fieldIndex}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {field.label}{field.isRequired && <span className="text-red-500">*</span>}
                          </label>
                          {field.fieldType === 'file' ? (
                            renderFileField(productIndex, field, productData, productKey)
                          ) : field.fieldType === 'dropdown' ? (
                            <select
                              value={productData[field.label] || ''}
                              onChange={(e) => handleFieldChange(productKey, field.label, e.target.value)}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required={field.isRequired}
                            >
                              <option value="">Select...</option>
                              {field.options && field.options.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : field.fieldType === 'date' ? (
                            <input
                              type="date"
                              value={productData[field.label] || ''}
                              onChange={(e) => handleFieldChange(productKey, field.label, e.target.value)}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required={field.isRequired}
                            />
                          ) : field.fieldType === 'number' ? (
                            <input
                              type="number"
                              value={productData[field.label] || ''}
                              onChange={(e) => handleFieldChange(productKey, field.label, e.target.value)}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required={field.isRequired}
                            />
                          ) : (
                            <input
                              type="text"
                              value={productData[field.label] || ''}
                              onChange={(e) => handleFieldChange(productKey, field.label, e.target.value)}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required={field.isRequired}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Product Selection Modal */}
      {showProductModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Product</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {availableProducts.map((product) => (
                  <button
                    key={product._id}
                    type="button"
                    onClick={() => handleProductSelect(product)}
                    className="w-full text-left p-3 border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <div className="font-medium text-gray-900">{product.name}</div>
                    {product.description && (
                      <div className="text-sm text-gray-600 mt-1">{product.description}</div>
                    )}
                  </button>
                ))}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowProductModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement; 